import CurrencyIcon from "@/components/icons/CurrencyIcon";
import ImgTag from "@/components/Helpers/ImgTag";
import Link from "next/link";
import { useState } from "react";
import useClient from "@/hooks/global/useClient";
import { useTranslation } from "react-i18next";
import TabbyModal from "@/components/Modals/TabbyModal";
import { getTabbyPromoUrl } from "@/utils/tabby";
import TamaraModal from "./TamaraModal";
import MispayModal from "./MispayModal";
import MadfuWidgetModal from "./MadfuWidgetModal";
import ApsInstallmentModal from "./ApsInstallmentModal";
import EmkanModal from "./EmkanModal";
import { EMKAN_CODE } from "@/constants/payments";

const WIDGET_FALLBACK_LOGOS = {
  aps_installment: "/imgs/installment-bank/aps-installment-widget-logo.png",
};

const InstallmentsWidgets = ({ amount, locale, paymentWidgets }) => {
  const { isClient } = useClient();
  const { t } = useTranslation("common");
  const [activePopup, setActivePopup] = useState(null);

  const getWidgetType = (code) => {
    switch (code) {
      case "tabby":
        return "popup";
      case "tamara":
        return "redirect";
      case "mispaymethod":
        return "popup";
      case "madfu_checkout":
        return "popup";
      case "bs_checkout":
        return "redirect";
      case "aps_installment":
        return "popup";
      case EMKAN_CODE:
        return "popup";
      default:
        return null;
    }
  };

  const getLearnMoreUrl = (code) => {
    switch (code) {
      case "tamara":
        return "/tamara";
      case "bs_checkout":
        return "/ezpay-terms-condition";
      default:
        return null;
    }
  };

  const renderLearnMore = (paymentWidget) => {
    const { code } = paymentWidget;

    const widget_type = getWidgetType(code);
    const learn_more_url = getLearnMoreUrl(code);

    if (widget_type === "redirect" && learn_more_url) {
      return (
        <Link
          href={learn_more_url}
          target="_blank"
          className="underline text-[7px] md:text-[12px] cursor-pointer ms-3 whitespace-nowrap"
        >
          {locale === "en" ? "Discover more" : "اكتشف المزيد"}
        </Link>
      );
    }

    if (widget_type === "popup") {
      if (code === "tabby" && !getTabbyPromoUrl(paymentWidget.more_info)) {
        return null;
      }

      return (
        <span
          className="underline text-[7px] md:text-[12px] cursor-pointer ms-3 whitespace-nowrap"
          onClick={(e) => {
            e.stopPropagation();
            setActivePopup(code);
          }}
        >
          {locale === "en" ? "Discover more" : "اكتشف المزيد"}
        </span>
      );
    }

    return null;
  };

  const renderWidgetContent = (paymentWidget) => {
    const { code, description, logo_url, title } = paymentWidget;
    const desc_text = description?.split(/SAR/i);

    if (!code) return null;

    const fallbackLogo = WIDGET_FALLBACK_LOGOS[code];
    const resolvedLogo = logo_url || fallbackLogo;

    if (!resolvedLogo) return null;
    if (!fallbackLogo && (!description || !title)) return null;

    return (
      <div
        key={code}
        className="flex items-center px-1 border rounded-md md:min-h-[55px] md:max-h-[55px] min-h-[41px] max-h-[41px]"
      >
        <ImgTag
          width={100}
          height={28}
          className="h-5 md:h-[28px] w-[51px] md:w-[67px] object-contain"
          src={resolvedLogo}
          alt={title || code}
        />

        <span className="text-[10px] md:text-[15px] ms-3 md:ms-[5px] font-medium">
          <span>
            {desc_text?.length === 2 ? (
              <>
                <span>{desc_text?.[0]}</span>
                <CurrencyIcon className="size-1.5 md:size-3 mx-0.5 inline" />
                <span>{desc_text?.[1]}</span>
              </>
            ) : (
              <span>{description}</span>
            )}
            {renderLearnMore(paymentWidget)}
          </span>
        </span>
      </div>
    );
  };

  const renderPopupModal = () => {
    if (!activePopup) return null;

    const closeModal = () => setActivePopup(null);
    // Read from the current list so a refetch (new total, Tabby dropped) reaches an open popup.
    const activeWidget = paymentWidgets?.find(
      (widget) => widget.code === activePopup,
    );

    switch (activePopup) {
      case "tabby":
        return (
          <TabbyModal
            src={getTabbyPromoUrl(activeWidget?.more_info)}
            setShow={closeModal}
          />
        );
      case "tamara":
        return (
          <TamaraModal
            show={true}
            setShow={closeModal}
            locale={locale}
            amount={amount}
          />
        );
      case "mispaymethod":
        return (
          <MispayModal
            amount={amount}
            locale={locale}
            show={true}
            setShow={closeModal}
          />
        );
      case "madfu_checkout":
        return (
          <MadfuWidgetModal
            t={t}
            locale={locale}
            show={true}
            setShow={closeModal}
          />
        );
      case "aps_installment":
        return (
          <ApsInstallmentModal
            locale={locale}
            show={true}
            setShow={closeModal}
          />
        );
      case EMKAN_CODE:
        return <EmkanModal onClose={closeModal} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-2 my-2">
      {paymentWidgets?.map((widget) => renderWidgetContent(widget))}
      {isClient && renderPopupModal()}
    </div>
  );
};

export default InstallmentsWidgets;
